import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { RESULT_GRID_NO_ZEBRA } from './resultGrids';
import { ExportMenu } from '../../components/AppToolbar/ExportMenu';
import { FormTitle } from '../../components/FormTitle';
import { BusyIcon } from '../../components/loading';
import { useHotkeys, formatCombo, type HotkeyDef } from '../../components/hotkeys';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { useConfirm, showDialog } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import { StepHint } from '../../components/StepHint/StepHint';
import { fmtLocalDateTime, fmtLocalShort } from '../../utils/datetime';
import { TrendIcon, TrendChartPopup, resolveRoutes, type TrendRoute, type TrendRow } from '../trend';
import { useRawDataForBatches, resolveRawDataFileOid, downloadRawDataFile } from './rawDataPreview';
import { useNavActions } from '../../hooks/useNavActions';
import { formatSdmsNumeric } from './sdmsNumericFormat';
// Permit Limit + Alert columns and the permit row tint (RTCFR "Add Permit Limit, Alert
// Column & Row Color Logic in Result Tracking", 2026-10-13) — the SAME shared helpers
// Result Entry Express uses, so the same sample + parameter shows the same limits and
// the same row colour on both forms (standing rule 8; no RT-specific rule).
import { formatLimitsInline, parsePermitLimits, permitAlertText, permitResultValue, permitRowClassFor, type PermitLimit } from './permitLimit';
import { usePermitRowColors } from './permitRowColors';
import { renderPermitAlertCell, renderPermitLimitCell } from './permitLimitCell';
import { permitLimitAutoFit, PERMIT_LIMIT_WIDTH } from './permitLimitWidth';
import '../backlog/backlog.css';
// Reuse the Result Approval / SDMS Result Validation tab styling
// (dv-tabs / dv-tab / dv-active) so RTT's Sample / QC tabs look identical.
import './SdmsPackageDetail.css';

/** The Result Tracking list row that "Load" opens.  In QCBatchID mode `id` is the
 *  analytical batch Oid (SpreadSheetEntry_AnalyticalBatch.Oid); in JobID mode (`isJob`)
 *  `id` is the job's Samplecheckin.Oid and the detail loads ALL the job's results. */
export interface ResultTrackingBatch {
  id:        string;
  qcBatchID: string | null;
  client:    string | null;
  projectID: string | null;
  jobID:     string | null;
  /** JobID-mode Load — `id` is a Samplecheckin.Oid; fetch the whole-job results. */
  isJob?:    boolean;
}

// Per-sample result row — the full ALPACA ResultView column set returned by
// AI_Analysis_ResultTracking_Samples_SP.  `isQc` drives which tab (Sample / QC
// Sample) a row belongs to. sampleName/mdl (2026-09-02) were already returned
// by the SP but unmapped here — no API change, just reading fields that were
// already on the wire (confirmed via sys.dm_exec_describe_first_result_set
// against the live SP).
interface TrackSampleRow {
  id:                string;
  jobID:             string | null;   // stamped from the batch (job-scoped grid); see load()
  qcBatchID:         string | null;
  uqabid:            string | null;   // analytical-batch Oid — scopes the shared Trend popup
  testMethodOid:     string | null;   // Test+Matrix+Method Oid — scopes the Trend QC/surrogate/ISTD series
  sys:               string | null;
  sampleId:          string | null;   // linked sample code (QC/Surrogate/ISTD tabs); null for non-sample QC
  sampleName:        string | null;
  siteName:          string | null;
  samplingAddress:   string | null;
  test:              string | null;
  method:            string | null;
  parameter:         string | null;
  numericResult:     string | null;
  numericDecimals?:  string | null;   // the row's SDMS Decimal setting (display rounding only)
  result:            string | null;
  rawData:           string | null;
  units:             string | null;
  df:                string | null;
  finalVolume:       string | null;
  rec:               string | null;
  rpd:               string | null;
  spikeAmount:       string | null;
  spikeAmountUnit:   string | null;
  recHCLimit:        string | null;
  recLCLimit:        string | null;
  rpdHCLimit:        string | null;
  rpdLCLimit:        string | null;
  rl:                string | null;
  ucl:               string | null;   // UCLLimit — Blank QC control limit; QC tab, Blank rows only
  mdl:               string | null;
  loq:               string | null;
  uql:               string | null;
  analyzedDate:      string | null;
  analyzedBy:        string | null;
  sdmsValidatedDate: string | null;
  sdmsValidatedBy:   string | null;
  statusText:        string | null;
  statusId:          string | null;
  validatedDate:     string | null;
  validatedBy:       string | null;
  approvedDate:      string | null;
  approvedBy:        string | null;
  matrix:            string | null;
  methodNumber:      string | null;
  component:         string | null;
  client:            string | null;
  qcType:            string | null;
  isSurrogate:        boolean;   // Testparameter.Surroagate — Surrogate tab
  isInternalStandard: boolean;   // Testparameter.InternalStandard — Internal Standard tab
  isQc:              boolean;
  // Permit limits (RTCFR 2026-10-13): the SP's PermitLimitsJson, parsed ONCE in load()
  // into `permitLimits` and then dropped (so the grid search never matches raw JSON).
  // undefined = the job carries no permit (QC-control rows never do).
  permitLimitsJson?: string | null;
  permitLimits?:     PermitLimit[];
  // Alert — the row's own Alert value (NULL from the SP today); the column shows
  // "⚑ Notify lab" / "—" from the permit limits instead (permitAlertState).
  alert?:            string | null;
}

// The Result Tracking detail's result tabs (task RTCFR, 2026-09-08). Sample / QC are
// the isQc split; Surrogate / Internal Standard are the parameter-classification tabs
// (Testparameter.Surroagate / .InternalStandard from the Test setup), shown BESIDE QC.
type ResultTab = 'Sample' | 'QC Sample' | 'Surrogate' | 'Internal Standard';
const RESULT_TABS: readonly ResultTab[] = ['Sample', 'QC Sample', 'Surrogate', 'Internal Standard'];
const RESULT_TAB_LABEL: Record<ResultTab, string> = {
  'Sample': 'Sample Results', 'QC Sample': 'QC Results',
  'Surrogate': 'Surrogate', 'Internal Standard': 'Internal Standard',
};

// ALPACA parity: empty cells render BLANK (no '—' placeholder).
function dt(v?: string | null): string {
  return v ? (fmtLocalDateTime(v) || '') : '';
}
function txt(field: keyof TrackSampleRow, header: string, width?: number): AppColumn<TrackSampleRow> {
  return { field: field as string, header, ...(width ? { width } : {}),
    text: r => (r[field] as string | null) ?? '', render: r => (r[field] as string | null) ?? '' };
}
function dcol(field: keyof TrackSampleRow, header: string): AppColumn<TrackSampleRow> {
  return { field: field as string, header,
    text: r => dt(r[field] as string | null), render: r => dt(r[field] as string | null) };
}

// ── Sample tab columns (user request 2026-09-02) ────────────────────────────
// Same header/order/width as REE's ResultEntryDetail.sampleCols for every field
// the two grids share; Result Tracking's own extra audit/tracking columns
// (SDMS/Result Validated/Approved, MethodNumber, %Rec, RL) — not part of REE's
// entry grid — are appended after, so Tracking keeps its full lifecycle
// visibility. AnalyzedDate uses fmtLocalShort to match REE's exact rendering.
// Leading JobID column (user request 2026-09-05) — Result Tracking is JOBID-grained
// (JOBID-pinned), so every loaded row belongs to the one selected job. The samples
// SP returns no standalone JobID, so it is stamped from the batch in load(). JobID
// first, then QBatchID — matching Result Approval / Result Validation.
const JOBID_COL: AppColumn<TrackSampleRow> = txt('jobID', 'JobID', 130);

// NumericResult rounded to the row's SDMS Decimal setting (task RTCFR, 2026-10-13) — the
// same rule as the SDMS Batch Results popup (formatSdmsNumeric: round, then trim trailing
// zeros). Display only: the stored value, the Result column and the permit-limit checks
// are untouched. With no Decimal setting the value shows as stored, as before.
const NUMERIC_RESULT_COL: AppColumn<TrackSampleRow> = {
  field: 'numericResult', header: 'NumericResult', width: 120,
  text:   r => formatSdmsNumeric(r.numericResult, r.numericDecimals, null),
  render: r => formatSdmsNumeric(r.numericResult, r.numericDecimals, null),
};

const SAMPLE_COLS: AppColumn<TrackSampleRow>[] = [
  // ── REE-matched (ResultEntryDetail.sampleCols) ──
  JOBID_COL,
  txt('qcBatchID',     'QBatchID',    110),
  txt('sys',           'SampleID',    140),
  txt('sampleName',    'SampleName',  130),
  txt('siteName',      'SiteName',    130),
  txt('test',          'Test',        150),
  txt('method',        'Method',      120),
  txt('parameter',     'Parameter',   140),
  NUMERIC_RESULT_COL,
  txt('result',        'Result',      120),
  txt('units',         'Units',       80),
  txt('rawData',       'RawData',     90),
  txt('df',            'D.F.',        70),
  txt('finalVolume',   'FinalVolume', 80),
  txt('mdl',           'MDL',         80),
  txt('loq',           'LOQ',         80),
  txt('uql',           'UQL',         80),
  // PermitLimit + Alert — same place, header, width rule and rendering as REE's
  // sampleCols (after UQL, before AnalyzedDate). Every applicable limit on one line,
  // red where the result is outside it; width follows the content.
  { field: 'permitLimit', header: 'PermitLimit', width: PERMIT_LIMIT_WIDTH.min,
    autoFit: permitLimitAutoFit<TrackSampleRow>(r => r.permitLimits, () => ''),
    text: r => (r.permitLimits?.length ? formatLimitsInline(r.permitLimits) : ''),
    render: r => renderPermitLimitCell(r.permitLimits, permitResultValue(r.result, r.numericResult), r.units, '') },
  // Alert — "⚑ Notify lab" when a permit limit is violated (shared permitAlertState,
  // RTCFR 2026-10-13) — the same cell as REE / RV / RA.
  { field: 'alert', header: 'Alert', width: 110,
    text: r => permitAlertText(r.permitLimits, r.result, r.numericResult, r.units, r.alert),
    render: r => renderPermitAlertCell(r.permitLimits, r.result, r.numericResult, r.units, r.alert) },
  { field: 'analyzedDate', header: 'AnalyzedDate', width: 150,
    text: r => r.analyzedDate ? fmtLocalShort(r.analyzedDate) : '',
    render: r => r.analyzedDate ? fmtLocalShort(r.analyzedDate) : '' },
  txt('analyzedBy',    'AnalyzedBy',  140),
  { field: 'statusText', header: 'Status', width: 140, type: 'status', text: r => r.statusText ?? '' },
  txt('statusId',      'StatusID',    80),
  txt('matrix',        'SampleMatrix', 90),
  txt('component',     'Component',   100),
  txt('client',        'Client',      130),
  // ── Result Tracking's own extra columns (not in REE's entry grid) ──
  // %Rec removed from the Sample Results tab (RTCFR column clean-up 2026-09-15 —
  // recovery is a QC metric, blank on a client sample; matches the SDMS Data
  // Package Sample tab which also hides it).
  txt('rl',                 'RL'),
  dcol('sdmsValidatedDate', 'SDMSValidatedDate'),
  txt('sdmsValidatedBy',    'SDMSValidatedBy'),
  dcol('validatedDate',     'ResultValidatedDate'),
  txt('validatedBy',        'ResultValidatedBy'),
  dcol('approvedDate',      'ResultApprovedDate'),
  txt('approvedBy',         'ResultApprovedBy'),
  txt('methodNumber',       'MethodNumber'),
];

// ── QC Sample tab columns ────────────────────────────────────────────────────
// Same header/order/width as REE's qcCols for every shared field (incl. the
// combined RPD/Rec control-limit ranges); Tracking's extra columns (SiteName /
// SamplingAddress, SDMS/Result Validated/Approved, MethodNumber, Component,
// Client) are appended after.
const QC_COLS: AppColumn<TrackSampleRow>[] = [
  // ── REE-matched (ResultEntryDetail.qcCols) ──
  JOBID_COL,
  txt('qcBatchID',     'QBatchID',      130),
  txt('sys',           'SysSampleCode', 150),
  // Linked parent sample (task RTCFR, 2026-09-10) — shown on QC / Surrogate / ISTD
  // (SURR_COLS / ISTD_COLS inherit it); blank for non-sample QC. Bound from the SP's
  // relationship-sourced SampleId, never parsed from SysSampleCode.
  txt('sampleId',      'SampleID',      140),
  txt('qcType',        'QCType',        90),
  txt('test',          'Test',          150),
  txt('method',        'Method',        120),
  txt('parameter',     'Parameter',     140),
  NUMERIC_RESULT_COL,
  txt('result',        'Result',        120),
  txt('units',         'Units',         80),
  txt('rawData',       'RawData',       90),
  txt('df',            'D.F.',          70),
  txt('rl',            'RL',            70),
  txt('ucl',           'UCLLimit',      90),   // Blank QC control limit, after RL (RTCFR Phase 2, 2026-09-15)
  txt('mdl',           'MDL',           80),
  txt('loq',           'LOQ',           80),
  txt('uql',           'UQL',           80),
  // SpikeAmount is a numeric value → right-aligned (ALPACA parity).
  { field: 'spikeAmount', header: 'SpikeAmount', width: 100, align: 'right',
    text: r => r.spikeAmount ?? '', render: r => r.spikeAmount ?? '' },
  txt('spikeAmountUnit', 'SpikeAmountUnit', 120),
  txt('rpd',            '%RPD', 70),
  txt('rec',            '%Rec', 70),
  // RPD is one-sided (never negative), so only the upper limit is shown (owner
  // decision 2026-09-25) — the low limit (e.g. "-20") is not displayed.
  { field: 'rpdHCLimit', header: 'RPD Ctrl Limit', width: 110,
    text: r => r.rpdHCLimit ?? '',
    render: r => r.rpdHCLimit ?? '' },
  { field: 'recLCLimit', header: 'Rec Ctrl Limit', width: 110,
    text: r => [r.recLCLimit, r.recHCLimit].filter(Boolean).join(' – '),
    render: r => [r.recLCLimit, r.recHCLimit].filter(Boolean).join(' – ') },
  { field: 'analyzedDate', header: 'AnalyzedDate', width: 150,
    text: r => r.analyzedDate ? fmtLocalShort(r.analyzedDate) : '',
    render: r => r.analyzedDate ? fmtLocalShort(r.analyzedDate) : '' },
  txt('analyzedBy',    'AnalyzedBy', 140),
  { field: 'statusText', header: 'Status', width: 140, type: 'status', text: r => r.statusText ?? '' },
  txt('statusId',      'StatusID',   80),
  txt('matrix',        'SampleMatrix', 90),
  // ── Result Tracking's own extra columns (not in REE's entry grid) ──
  txt('siteName',           'SiteName'),
  txt('samplingAddress',    'SamplingAddress'),
  dcol('sdmsValidatedDate', 'SDMSValidatedDate'),
  txt('sdmsValidatedBy',    'SDMSValidatedBy'),
  dcol('validatedDate',     'ResultValidatedDate'),
  txt('validatedBy',        'ResultValidatedBy'),
  dcol('approvedDate',      'ResultApprovedDate'),
  txt('approvedBy',         'ResultApprovedBy'),
  txt('methodNumber',       'MethodNumber'),
  txt('component',          'Component'),
  // Client removed from QC / Surrogate / Internal Standard (RTCFR column clean-up
  // 2026-09-15) — a QC sample has no client-context column; matches the SDMS Data
  // Package which hides Client on these tabs. (Sample tab keeps its own Client.)
];

// Surrogate / Internal Standard tabs trim QC_COLS (user request 2026-09-09):
//   • Surrogate — drop RL · MDL · LOQ · UQL · %RPD · RPD Ctrl Limit
//                 (keep %Rec + Rec Ctrl Limit — surrogate recovery is the metric).
//   • Internal Standard — drop those PLUS %Rec, D.F. and Rec Ctrl Limit.
// Filtering the shared QC_COLS keeps column order / alignment / export identical.
const SURR_DROP = new Set(['rl', 'ucl', 'mdl', 'loq', 'uql', 'rpd', 'rpdHCLimit']);
const ISTD_DROP = new Set(['df', 'rl', 'ucl', 'mdl', 'loq', 'uql', 'rpd', 'rec', 'rpdHCLimit', 'recLCLimit']);
// QC Sample Results tab additionally drops SiteName (RTCFR column clean-up 2026-09-15) —
// a QC sample carries no client-site context. Surrogate / Internal Standard KEEP SiteName
// (it is NOT in their removal list), so this applies to the QC tab only, not the shared
// QC_COLS base the Surr/ISTD sets filter from.
const QC_DROP = new Set(['siteName']);
const SURR_COLS: AppColumn<TrackSampleRow>[] = QC_COLS.filter(c => !SURR_DROP.has(c.field as string));
const ISTD_COLS: AppColumn<TrackSampleRow>[] = QC_COLS.filter(c => !ISTD_DROP.has(c.field as string));

// Map an RT grid row → the shared Trend router's TrendRow (task RTCFR, 2026-09-10).
// Reuses the SAME routing/popup the SDMS Result Validation + REE grids use — RT does
// NOT re-implement trend logic. Sample → site trend (siteName); sample-linked QC →
// parent sample (sampleId); Surrogate / Internal Standard → their series (flags +
// testMethodOid).
function rtToTrendRow(r: TrackSampleRow): TrendRow {
  return {
    id:                 r.id,
    sys:                r.sys || null,
    parameter:          r.parameter || null,
    siteName:           r.siteName || null,
    qcTypeName:         r.qcType || null,
    parentSys:          r.sampleId || null,
    testMethodOid:      r.testMethodOid || null,
    isInternalStandard: r.isInternalStandard ?? false,
    isSurrogate:        r.isSurrogate ?? false,
    analyzedDate:        r.analyzedDate ?? null,
  };
}

interface Props {
  /** The batch whose results to show. May be null in EMBEDDED mode, in which
   *  case the grid renders EMPTY (no fetch) — e.g. on Result Tracking page load
   *  before a QC Batch is picked (user request 2026-09-03). Full-page callers
   *  always pass a batch. */
  batch: ResultTrackingBatch | null;
  /** Return to the Result Tracking list. */
  onClose: () => void;
  /** Render ONLY the Sample/QC tabs (+ inline Search) + grid (no page chrome),
   *  for embedding inline in the Result Tracking list page's grid slot (user
   *  request 2026-09-03). In this mode the Refresh / Export To / Rollback controls
   *  live in the LIST page's top bar, driven via {@link embedApiRef} +
   *  {@link onEmbedState}; only Search sits here, next to the tabs. */
  embedded?: boolean;
  /** (embedded) Imperative handle so the list page's top bar can trigger this
   *  grid's Refresh (reload samples) and Rollback (ticked rows). */
  embedApiRef?: React.MutableRefObject<{ refresh: () => void; rollback: () => void } | null>;
  /** (embedded) Publishes this grid's reactive state (selection count, rollback
   *  permission, export payload) up to the list page so its top-bar Export To /
   *  Rollback render correctly. */
  onEmbedState?: (s: RtEmbedState) => void;
  /** (embedded) The selected Result Tracking status chip. When set (and not
   *  'all'), the Sample/QC grids show ONLY rows at that status — a job can carry
   *  samples across several stages, and the analyst who picked e.g. "Pending
   *  Report Validation" wants to see only those rows, not the whole job (user
   *  request 2026-09-05). Row StatusText matches the chip label (both SPs apply
   *  the same RP-* disambiguation). 'all'/undefined = show every row. */
  statusFilter?: string | null;
  /** (embedded) WINDOW mode (task RTCFR, 2026-09-05): a `yyyy-MM-dd` since-date. When
   *  set (and `batch` is null), the grid loads EVERY signed-off sample row for ALL jobs
   *  received within the window (GET /result-tracking/results), so Load shows every job
   *  ID's samples — then statusFilter narrows to the selected chip. Each row carries its
   *  own JobID from the SP. null = the classic single-batch/job load via `batch`. */
  windowSince?: string | null;
  /** (embedded, window mode) Query-panel filters applied SERVER-SIDE to the grid rows
   *  (task RTCFR, 2026-09-07) so the Sample/QC grids show only matching data — not
   *  just the dropdowns. Keys: test/matrix/sampleLocation/client/projectId/projectName/
   *  jobId/batchId ('|'-joined), analyzedFrom/To, receivedFrom/To, collectedFrom/To.
   *  Passed through to GET /result-tracking/results as query params. */
  windowFilters?: Record<string, string>;
}

/** Reactive state the embedded grid publishes to its host list page. */
export interface RtEmbedState {
  selectedCount: number;
  canRollback:   boolean;
  hasBatch:      boolean;
  /** True while the grid panel is maximized (full-page). The host hides its
   *  top-bar Rollback then — the detail renders Rollback beside the Minimize
   *  button instead (user request 2026-09-03). */
  maximized:     boolean;
  exportFilename: string;
  exportHeaders:  string[];
  exportRows:     string[][];
  /** The grid's own data-load time (ms) + in-flight flag, published so the host
   *  FormTitle "Loaded in X" chip reflects the ACTUAL samples fetch — not the
   *  (cached, ~0 ms) status-list load (user report 2026-09-05). */
  loadMs:        number | null;
  loading:       boolean;
}

/**
 * Result Tracking → "View" drill-in (READ-ONLY).  Ports ALPACA's Result Tracking
 * Retrieve: the selected QC batch's individual result rows, structured the SAME
 * way as REE's ResultEntryDetail (user request 2026-09-02) — a Sample / QC
 * Sample tab bar instead of a "Selection Mode" dropdown, each tab using REE's
 * column order/headers/widths (plus Result Tracking's own extra audit columns
 * appended after) — with a leading row-selection checkbox column driving Rollback.
 */
export default function ResultTrackingDetail({ batch, onClose, embedded = false, embedApiRef, onEmbedState, statusFilter, windowSince, windowFilters }: Props) {
  usePermitRowColors();   // Settings ▸ Permit Row Colors — re-colour the rows when the lab's rules load
  const [rows, setRows]       = useState<TrackSampleRow[]>([]);
  // Shared Trend wiring (task RTCFR, 2026-09-10) — same router + popup as SDMS/REE.
  const trendRouteMap = useMemo(() => resolveRoutes(rows.map(rtToTrendRow)), [rows]);
  const [openTrend, setOpenTrend] = useState<
    { route: TrendRoute; label: string; batchOid: string; method: string | null } | null
  >(null);
  const [loading, setLoading] = useState(true);
  // Monotonic request id — only the latest load() may write rows/error/loading (guards
  // against out-of-order responses clobbering the grid; see load()).
  const reqSeq = useRef(0);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [error, setError]     = useState<string | null>(null);
  // Sample / QC Sample tabs (REE parity) — same two labels as ResultEntryDetail's
  // tab bar. Filters the loaded rows (isQc); each tab has its own column set.
  const [selMode, setSelMode] = useState<ResultTab>('Sample');
  // Leading checkbox column (ALPACA parity) — also drives the Rollback action.
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // Maximize/Restore the embedded grid panel (REE parity) — fullscreens the panel
  // as a fixed overlay; Esc or the Minimize icon restores. (user request 2026-09-03)
  const [maximized, setMaximized] = useState(false);
  const notify = useNotify();
  const { prompt } = useConfirm();
  // Shared RawData Preview (task RTCFR, 2026-09-10) — reuses the SDMS Result Validation
  // datasource / lookup / download (features/analysis/rawDataPreview). RT spans a whole
  // job/window across several batches, so raw-data availability is fetched per DISTINCT
  // batch (row.uqabid) and a row resolves its file by (batch, SysSampleCode) exactly like
  // SDMS. When a file exists → clickable Preview; otherwise the cell keeps its N/A text.
  const rawBatchOids = useMemo(
    () => rows.map(r => r.uqabid).filter((x): x is string => !!x), [rows]);
  const rawDataByBatch = useRawDataForBatches(rawBatchOids);
  const previewRawData = useCallback(async (r: TrackSampleRow) => {
    const info = r.uqabid ? rawDataByBatch.get(r.uqabid) : undefined;
    const oid = resolveRawDataFileOid(info, r.sys ?? '');
    if (!oid) { notify.warning('Raw data is not available for this sample.'); return; }
    try {
      await downloadRawDataFile(oid, info?.files.find(f => f.fileOid === oid)?.fileName);
    } catch (err) {
      console.warn('[RawData] download failed', oid, err);
      notify.error('Failed to download the raw data file. Please try again.');
    }
  }, [rawDataByBatch, notify]);

  // Rollback is a WRITE action — show it only when the user's Module Access
  // Control grants Write on Result Tracking (or they're an admin).  Uses the
  // shared per-form action gate (navActionsForForm('restrack') → Write); the
  // server enforces the same rule with a 403, so this is UX only.
  const canRollback = useNavActions('restrack').write;

  const load = useCallback(async () => {
    // Nothing to load: no batch AND no window since-date → EMPTY grid, no fetch.
    if (!batch && !windowSince) { reqSeq.current++; setRows([]); setSelectedIds(new Set()); setError(null); setLoadMs(null); setLoading(false); return; }
    // Stale-response guard (user report 2026-09-09: RTT grid intermittently empty). Fast
    // filter/status/date changes fire overlapping fetches; without a sequence check a
    // slower EARLIER response (or a superseded request's error) resolves last and
    // clobbers the grid with stale/empty rows. Only the LATEST request may write state.
    const myReq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      // WINDOW mode (all jobs in the window) when a since-date is given and no batch;
      // else JobID mode (whole job) / QCBatchID mode (one batch).
      let url = windowSince && !batch
        ? `/api/v1/analysis/result-tracking/results?sinceDate=${encodeURIComponent(windowSince)}`
        : batch!.isJob
          ? `/api/v1/analysis/result-tracking/job/${batch!.id}/results`
          : `/api/v1/analysis/result-tracking/${batch!.id}/results`;
      // Window mode: append the query-panel filters so the grid rows themselves are
      // filtered server-side (task RTCFR, 2026-09-07).
      if (windowSince && !batch && windowFilters) {
        for (const [k, v] of Object.entries(windowFilters))
          if (v) url += `&${k}=${encodeURIComponent(v)}`;
      }
      const r = await api.get(url);
      if (myReq !== reqSeq.current) return;   // superseded by a newer load — drop stale rows
      const data = (r.data as { data: TrackSampleRow[] }).data ?? [];
      // Leading JobID column: the SP now returns each row's own JobID (window mode
      // spans many jobs). Fall back to the batch's JobID for QC-control rows (no
      // Samplelogin → SP JobID null) in the single-batch/job modes (user request
      // 2026-09-05). Mirrors Result Approval / Result Validation.
      setRows(data.map(({ permitLimitsJson, ...row }) => ({
        ...row,
        jobID: row.jobID ?? batch?.jobID ?? null,
        // Permit limits parsed once here (undefined = no permit) — RTCFR 2026-10-13.
        permitLimits: parsePermitLimits(permitLimitsJson),
      })));
      setSelectedIds(new Set());
    } catch {
      if (myReq !== reqSeq.current) return;   // superseded — don't clobber the fresh grid with an error
      setRows([]);
      setError('Failed to load results — check the API logs.');
    } finally {
      if (myReq === reqSeq.current) {
        setLoadMs(performance.now() - t0);
        setLoading(false);
      }
    }
  }, [batch, windowSince, windowFilters]);

  useEffect(() => { void load(); }, [load]);

  // Esc restores from the maximized (full-page) grid panel — via the shared
  // hotkey layer (standing rule 14), never a raw keydown listener.
  useHotkeys(maximized
    ? { combo: 'esc', label: 'Exit full screen', group: 'View', run: () => setMaximized(false) }
    : null);

  // Browser Back closes the detail (parity with the Result Validation detail).
  // Skipped when embedded inline — there is no navigation to pop back from.
  useEffect(() => {
    if (embedded) return;
    window.history.pushState({ rtDetail: true }, '');
    const onPop = () => onClose();
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [onClose, embedded]);

  // Rollback the ticked result rows (safe subset) — ALPACA ResultEntryViewController
  // Rollback_Execute: guard pre-check (fires BEFORE the reason popup) → mandatory
  // reason → revert to Pending Entry.  Reported rows are skipped server-side.
  const doRollback = useCallback(async () => {
    const ids = Array.from(selectedIds).map(String);
    if (ids.length === 0) { notify.info('Please select one or more result rows to roll back.'); return; }
    try {
      const pre = await api.post('/api/v1/analysis/result-tracking/rollback/precheck', { sampleParameterIds: ids });
      const d = pre.data as { blocked?: boolean; message?: string; eligible?: number; skippedReported?: number };
      if (d.blocked) { notify.warning(d.message ?? 'Rollback is blocked.'); return; }
      if ((d.eligible ?? 0) === 0) {
        notify.warning(d.skippedReported
          ? 'The selected rows are already Reported and cannot be rolled back.'
          : 'Nothing to roll back in the selected rows.');
        return;
      }
    } catch { /* precheck hiccup — the write path re-checks the guards */ }
    const reason = await prompt({
      title: 'Result Rollback',
      // Count spans every tab (the selection is cross-tab), so ticks on another tab are never silent.
      message: `Enter a reason for the rollback. The ${ids.length} selected result row${ids.length === 1 ? '' : 's'} (across all tabs) return to Pending Entry.`,
      placeholder: 'Rollback reason…',
      required: true,
      confirmLabel: 'Rollback',
    });
    if (reason === null) return;
    try {
      const res = await api.post('/api/v1/analysis/result-tracking/rollback', { sampleParameterIds: ids, reason });
      const d = res.data as { success: boolean; error?: string; rolledBack?: number; skippedReported?: number };
      if (!d.success) { notify.error(d.error ?? 'Rollback failed.'); return; }
      if ((d.rolledBack ?? 0) > 0) {
        // Milestone (§18.6) → centered modal, not the Tier-2 top banner.
        void showDialog({
          type: 'success',
          title: 'Rollback',
          message: `Rolled back successfully.${d.skippedReported ? ` (${d.skippedReported} Reported skipped.)` : ''}`,
        });
        setSelectedIds(new Set());
        void load();
      } else {
        notify.warning('Nothing was rolled back (Reported rows are skipped).');
      }
    } catch {
      notify.error('Rollback failed — check the API logs.');
    }
  }, [selectedIds, notify, prompt, load]);

  // Toolbar shortcuts (standing rule 14) — via the shared hotkey layer, mirroring the
  // visible buttons. Full-page has Refresh · Back · Rollback; embedded (maximized) has
  // Rollback only (Refresh/Back live on the host list page). The maximize-restore Esc
  // above stays as-is; Back's Esc is only bound full-page, where `maximized` is false.
  const rtKeys: HotkeyDef[] = [];
  if (!embedded) {
    rtKeys.push({ combo: 'alt+r', label: 'Refresh', group: 'View', order: 1, disabled: loading, run: () => void load() });
    rtKeys.push({ combo: 'esc', label: 'Back', group: 'View', order: 2, allowInInput: false, run: () => onClose() });
    if (canRollback) rtKeys.push({ combo: 'alt+b', label: 'Rollback', group: 'View', order: 3, disabled: selectedIds.size === 0, run: () => void doRollback() });
  } else if (maximized && canRollback) {
    rtKeys.push({ combo: 'alt+b', label: 'Rollback', group: 'View', order: 3, disabled: selectedIds.size === 0, run: () => void doRollback() });
  }
  useHotkeys(rtKeys);

  // Restrict to the selected status filter (user request 2026-09-05) — a job spans
  // stages, but the grid should show only rows at the picked status. statusFilter is a
  // single status caption OR a '|'-joined SET (the RTT Approved/Pending buckets, task
  // RTCFR 2026-09-11); match the row's StatusText against the set. Both SPs apply the
  // same RP-* disambiguation, so the captions line up.
  const statusRows = useMemo(() => {
    if (!statusFilter || statusFilter === 'all') return rows;
    const wanted = new Set(statusFilter.split('|').map(s => s.trim()).filter(Boolean));
    return rows.filter(r => wanted.has(r.statusText ?? ''));
  }, [rows, statusFilter]);
  // The row selection is CROSS-TAB (G5 parity — ALPACA shows Sample + QC in ONE grid,
  // so a Check All there never resets): switching Sample ↔ QC ↔ Surrogate ↔ ISTD keeps
  // every tick, and each tab's grid shows the checked state for its own rows only.
  // (Replaces the 2026-07-27 clear-on-tab-switch, which dropped a Check All and
  // disabled Rollback — user report 2026-09-25.) Only ticks on rows no longer loaded
  // (status filter changed) are pruned; the Rollback prompt states the ticked count.
  useEffect(() => {
    setSelectedIds(prev => {
      if (prev.size === 0) return prev;
      const live = new Set<string | number>(statusRows.map(r => r.id));
      const next = new Set([...prev].filter(id => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [statusRows]);
  // Four result tabs (task RTCFR, 2026-09-08), MUTUALLY EXCLUSIVE so no row appears in
  // more than one tab (user request: "don't overlap the records"). Priority: a Surrogate
  // parameter → Surrogate tab; an Internal Standard parameter → Internal Standard tab;
  // otherwise the isQc split (QC Results / Sample Results). Surrogate/ISTD are Test-setup
  // parameter flags and can sit on real samples OR QC controls, so they're pulled OUT of
  // the Sample/QC tabs into their own. (A parameter is never both surrogate and ISTD.)
  const surrogateRows = useMemo(() => statusRows.filter(r =>  r.isSurrogate), [statusRows]);
  const istdRows      = useMemo(() => statusRows.filter(r =>  r.isInternalStandard && !r.isSurrogate), [statusRows]);
  const sampleRows    = useMemo(() => statusRows.filter(r => !r.isQc && !r.isSurrogate && !r.isInternalStandard), [statusRows]);
  const qcRows        = useMemo(() => statusRows.filter(r =>  r.isQc && !r.isSurrogate && !r.isInternalStandard), [statusRows]);
  const rowsForTab = (t: ResultTab) =>
    t === 'Sample' ? sampleRows : t === 'QC Sample' ? qcRows
      : t === 'Surrogate' ? surrogateRows : istdRows;

  const activeRows = rowsForTab(selMode);
  // Sibling tabs (other than the active one) that DO have rows, with counts — used by the
  // empty-state hint so an empty tab points to where this selection's data actually is
  // (user report 2026-09-09). Counts are shown here only (the tab captions stay bare).
  const otherTabsWithRows = RESULT_TABS
    .filter(t => t !== selMode && rowsForTab(t).length > 0)
    .map(t => `${RESULT_TAB_LABEL[t]} (${rowsForTab(t).length})`);
  // Surrogate / Internal Standard carry recovery / limits like QC, so they reuse the QC
  // column set; only the Sample tab uses the plain result columns.
  const baseCols = selMode === 'Sample'            ? SAMPLE_COLS
                 : selMode === 'Surrogate'         ? SURR_COLS
                 : selMode === 'Internal Standard' ? ISTD_COLS
                 : QC_COLS.filter(c => !QC_DROP.has(c.field as string));   // QC tab drops SiteName
  // Trend column (task RTCFR, 2026-09-10) — inserted right AFTER RawData on every tab.
  // Reuses the shared <TrendIcon>/<TrendChartPopup> + route resolver (no trend logic in
  // RT). Enabled only when the row carries an analytical batch (uqabid) to scope the
  // batch-keyed series — same behaviour as the SDMS Result Validation / REE grids.
  const trendCol: AppColumn<TrackSampleRow> = {
    field: 'trend', header: 'Trend', width: 70, sortable: false, filterable: false,
    text: () => '',
    render: (r) => (
      <TrendIcon
        route={r.uqabid ? trendRouteMap.get(r.id) : undefined}
        onOpen={() => {
          const route = trendRouteMap.get(r.id);
          if (route?.routable && r.uqabid) {
            setOpenTrend({
              route,
              label: `${r.sys ?? ''}${r.parameter ? ` · ${r.parameter}` : ''}`,
              batchOid: r.uqabid,
              method: r.method,
            });
          }
        }}
      />
    ),
  };
  // RawData column → clickable Preview when SDMS raw data exists for the row's batch
  // (same lookup as SDMS Result Validation); otherwise the cell keeps its text (N/A or
  // filename). Export still emits the plain text via `text`. Replaces the module-level
  // txt('rawData') column at render (it can't reach rawDataByBatch / previewRawData).
  const rawDataCol: AppColumn<TrackSampleRow> = {
    field: 'rawData', header: 'RawData', width: 90,
    text: r => r.rawData ?? '',
    render: r => {
      const oid = r.uqabid ? resolveRawDataFileOid(rawDataByBatch.get(r.uqabid), r.sys ?? '') : null;
      return oid
        ? <a href="#" className="dv-raw-prev" onClick={e => { e.preventDefault(); void previewRawData(r); }}>Preview</a>
        : <>{r.rawData ?? 'N/A'}</>;
    },
  };
  const cols = useMemo(() => {
    const withRaw = baseCols.map(c => (c.field === 'rawData' ? rawDataCol : c));
    const i = withRaw.findIndex(c => c.field === 'rawData');
    return i >= 0 ? [...withRaw.slice(0, i + 1), trendCol, ...withRaw.slice(i + 1)]
                  : [...withRaw, trendCol];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseCols, trendRouteMap, rawDataByBatch]);

  // Grid-top fuzzy search (user request 2026-09-02) — client-side only, same
  // "match any string field" idiom used by the Result Tracking LIST page's own
  // search box, reproduced locally here (this detail page doesn't reuse any
  // AnalysisListPage code).
  const [detailSearch, setDetailSearch] = useState('');
  const searchedRows = useMemo(() => {
    const term = detailSearch.trim().toLowerCase();
    if (!term) return activeRows;
    return activeRows.filter(r =>
      Object.values(r as unknown as Record<string, unknown>).some(v =>
        typeof v === 'string' && v.toLowerCase().includes(term),
      ),
    );
  }, [activeRows, detailSearch]);
  useEffect(() => { setDetailSearch(''); }, [selMode]);

  // Header id — JobID in job mode, QCBatchID in batch mode (ALPACA {BO} – {record}).
  const headId = (batch?.isJob ? batch?.jobID : batch?.qcBatchID) ?? '';
  // Memoized so the embed-state publish effect below has stable deps (a fresh
  // object every render would loop the parent's setState).
  const exportData = useMemo(() => ({
    filename: `result-tracking-${headId || batch?.id || (windowSince ? 'all' : 'empty')}-${selMode.toLowerCase().replace(' ', '-')}`,
    ...gridExportMatrix(searchedRows, cols),
  }), [headId, batch?.id, windowSince, selMode, searchedRows, cols]);

  // (embedded) Expose Refresh / Rollback to the host list page's top bar, and
  // publish reactive state so its Export To / Rollback render correctly. All
  // deps are memoized/primitive, so this does not loop.
  useEffect(() => {
    if (embedApiRef) embedApiRef.current = { refresh: () => void load(), rollback: () => void doRollback() };
  }, [embedApiRef, load, doRollback]);
  useEffect(() => {
    if (!embedded) return;
    onEmbedState?.({
      // hasBatch = "a load context exists" → true for window mode too, so the host's
      // top-bar Export / Rollback enable once Load has run (task RTCFR 2026-09-05).
      selectedCount: selectedIds.size, canRollback, hasBatch: !!batch || !!windowSince, maximized,
      exportFilename: exportData.filename, exportHeaders: exportData.headers, exportRows: exportData.rows,
      loadMs, loading,
    });
  }, [embedded, onEmbedState, selectedIds, canRollback, batch, windowSince, maximized, exportData, loadMs, loading]);
  // ── Shared render pieces (defined once, used by both layouts) ──────────────
  // Result tabs (task RTCFR, 2026-09-08): Sample Results · QC Results · Surrogate ·
  // Internal Standard. Same folder-tab styling (Result Approval / SDMS Result
  // Validation parity); each shows its own match count.
  const tabButtons = RESULT_TABS.map(m => {
    const active = selMode === m;
    return (
      <div key={m} role="tab" aria-selected={active}
        className={`dv-tab ${active ? 'dv-active' : ''}`}
        onClick={() => setSelMode(m)}>
        {RESULT_TAB_LABEL[m]}
      </div>
    );
  });

  // Fuzzy Search box (embedded: next to the tabs; full-page: in the toolbar).
  const searchBox = (
    <div className="list-search" style={{ width: 260, flexShrink: 0 }}>
      <span className="tb-i">🔍</span>
      <input type="text" placeholder="Search…" value={detailSearch}
        onChange={e => setDetailSearch(e.target.value)} />
      {detailSearch && <span className="list-search-clear" onClick={() => setDetailSearch('')}>×</span>}
    </div>
  );

  // Grid card — plain `bl-card bl-card-flex` (row height matches the RT LIST grid);
  // top-left radius zeroed so the tab bar above connects to it.
  const gridJsx = (
    <div className="bl-card bl-card-flex" style={{ borderTopLeftRadius: 0 }}>
      {error ? (
        <div className="bl-empty" style={{ color: 'var(--ai-red)', flexDirection: 'column', gap: 6 }}>
          <i className="ti ti-alert-triangle" style={{ fontSize: 24, opacity: .35 }} />
          <div>{error}</div>
        </div>
      ) : (
        <AppDataGrid<TrackSampleRow>
          className={RESULT_GRID_NO_ZEBRA}
          rows={searchedRows}
          columns={cols}
          getRowId={(r) => r.id}
          loading={loading}
          loadMs={loadMs}
          /* Own .ree-maximize-btn + host Export — no duplicate grid cluster
             (user report 2026-09-23). */
          hideHeaderTools
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          stickyColumns={2}
          /* Permit-limit row tint — the SAME rule as REE / RV / RA (permitLimit.ts
             permitRowClassFor): the lab's rules from Settings ▸ Permit Row Colors
             reached; blank result or no permit keeps the default row colour. */
          getRowClassName={r => permitRowClassFor(r.permitLimits, r.result, r.numericResult, r.units, r.mdl)}
          emptyText={!batch && !windowSince
            ? 'Pick a QC Batch ID in the Query panel and click Load to view its samples.'
            : otherTabsWithRows.length
              // The active tab is empty but sibling tabs have rows (user report 2026-09-09:
              // "VOC → only QC loading, samples not loaded" — the VOC rows at this stage were
              // Surrogate/Internal Standard, which the exclusive partition routes to those
              // tabs, so Sample Results is legitimately empty). Point the user there.
              ? `No results in the ${RESULT_TAB_LABEL[selMode]} tab — this selection's data is on: ${otherTabsWithRows.join(' · ')}.`
              : `No results in the ${RESULT_TAB_LABEL[selMode]} tab.`}
        />
      )}
      {openTrend && (
        <TrendChartPopup
          route={openTrend.route}
          batchOid={openTrend.batchOid}
          sampleLabel={openTrend.label}
          method={openTrend.method}
          permitLimit={null}
          permitWarn={null}
          onClose={() => setOpenTrend(null)}
        />
      )}
    </div>
  );

  // Full-page body: its OWN toolbar (Refresh · Export to · Search) + tabs + grid.
  // (Full-page Rollback lives in the top action bar; see the return below.)
  const bodyJsx = (
    <>
      <div className="bl-card">
        <div className="ree-toolbar-row" style={{ padding: '8px 14px', marginBottom: 0 }}>
          <button className="ca-btn" onClick={() => void load()} disabled={loading} title={`Reload this batch's results (${formatCombo('alt+r')})`}>
            <BusyIcon busy={loading} icon="ti-refresh" /> Refresh
          </button>
          <ExportMenu filename={exportData.filename} headers={exportData.headers} rows={exportData.rows} />
          {searchBox}
        </div>
      </div>
      <div className="dv-tabs" role="tablist">{tabButtons}</div>
      {gridJsx}
    </>
  );

  // EMBEDDED (inside the Result Tracking list page's grid slot): NO toolbar (the
  // Refresh / Export to / Rollback controls live in the list page's top bar, via
  // embedApiRef + onEmbedState). Just the Sample/QC tabs with the fuzzy Search on
  // the right, then the grid. The wrapper flexes so the grid fills the slot.
  if (embedded) {
    return (
      // Maximized → fixed full-page overlay (REE parity, `.ree-detail-maximized`).
      <div className={maximized ? 'ree-detail-maximized' : undefined}
        style={maximized ? { padding: '10px 14px' } : { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* Tab row: tabs · Search (next to QC Sample) · [right] Rollback-when-maximized · Maximize/Minimize. */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
          <div className="dv-tabs" role="tablist">{tabButtons}</div>
          <div style={{ paddingBottom: 4 }}>{searchBox}</div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 4 }}>
            {/* When maximized there is no top bar in view, so Rollback rides beside
                Minimize (Write-gated, disabled until a row is ticked). */}
            {maximized && canRollback && (
              <button type="button" className="ca-btn" onClick={() => void doRollback()} disabled={selectedIds.size === 0}
                title={(selectedIds.size === 0
                  ? 'Tick one or more result rows to roll back.'
                  : 'Roll the ticked results back to Pending Entry (reason required).') + ` (${formatCombo('alt+b')})`}>
                <i className="ti ti-arrow-back-up" style={{ color: 'var(--ai-red)' }} aria-hidden="true" />&nbsp;Rollback
              </button>
            )}
            <button type="button" className="ree-maximize-btn"
              onClick={() => setMaximized(m => !m)}
              title={maximized ? 'Restore grid to inline view (Esc).' : 'Expand grid to full page.'}
              aria-label={maximized ? 'Restore grid' : 'Maximize grid'}>
              <i className={`ti ${maximized ? 'ti-arrows-minimize' : 'ti-arrows-maximize'}`} aria-hidden="true" />
            </button>
          </div>
        </div>
        {gridJsx}
      </div>
    );
  }

  return (
    <div className="bl-page">
      {/* Dynamic-Island stepper (BO UI Standard §5) — on the detail both steps are
          done: Select Batch ✓ → Load Samples ✓ (prototype Result-Entry). */}
      <TopbarStepper steps={[
        { id: 'select', label: 'Select Batch', state: 'done' },
        { id: 'load',   label: 'Load Samples', state: 'done' },
      ] as IslandStep[]} />
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Analysis</span><i className="ti ti-chevron-right" />
            <span onClick={onClose} style={{ cursor: 'pointer' }}>Result Tracking</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{headId || '—'}</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="restrack" title={`Result Tracking – ${headId}`}
              loadMs={loadMs} loading={loading} auditFormName="Result Tracking" />
            {/* TOP-RIGHT actions (Result Approval parity): Back · Rollback. */}
            <div className="dp-title-actions">
              <button className="ca-btn" onClick={onClose} title={`Back to the Result Tracking list (${formatCombo('esc')})`}>
                <i className="ti ti-arrow-left" /> Back
              </button>
              {/* Rollback is Write-gated: hidden for read-only users (their MAC
                  grants no Write on Result Tracking).  The endpoint enforces the
                  same rule, so this is UX only. */}
              {canRollback && (
                <button className="ca-btn" onClick={() => void doRollback()} disabled={selectedIds.size === 0}
                  title={(selectedIds.size === 0
                    ? 'Tick one or more result rows to roll back.'
                    : 'Roll the ticked results back to Pending Entry (reason required).') + ` (${formatCombo('alt+b')})`}>
                  <i className="ti ti-arrow-back-up" style={{ color: 'var(--ai-red)' }} /> Rollback
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        {/* Guidance strip — prototype (Result-Entry) detail recHint content, AI-accurate
            (Pending Entry).  Shared <StepHint> style, matching the list. */}
        <StepHint tone="guidance">
          <i className="ti ti-info-circle" /> <b>Tracking · read-only.</b> The full result record for
          this batch. Use <b>Rollback</b> (mandatory reason, audited) to return the results to
          <b> Pending Entry</b> for re-entry.
        </StepHint>
        {bodyJsx}
      </div>
    </div>
  );
}
